/**
 * The words for a ticket's app invite (GH-884, owner 2026-10-04: "Tickets should always come with
 * a app invite … each email gets an invite code"), in one place for the donor web, the app and the
 * emails. `apps/ticket-invite-copy.test.ts` holds the Swift twin (`TicketInviteCopy.swift`) to it.
 *
 * Rules the copy keeps:
 *   - labels are five words or fewer (`TICKET_INVITE_LABELS`); the reason for the guest field sits
 *     behind an ⓘ;
 *   - no scarcity: a checkout is not a membership surface (DEC-051), so nothing here counts
 *     invites "left";
 *   - the guest field says what the address is for and that nothing else is sent, because the
 *     buyer is giving us somebody else's address.
 */
export const TICKET_INVITE_COPY = {
    /* -------------------------------------------------------------------- the checkout */
    /** Beside the tickets, while invites come with them. */
    included: "Includes a GiveHere app invite",
    /** The guest fields' heading; its ⓘ carries `guestsExplain`. */
    guestsLabel: "Guests’ emails (optional)",
    guestsExplain: "Each guest gets their ticket and their own GiveHere app invite by email. We use the address for that one email only, then delete it. Leave one blank and that invite comes to you to pass on.",
    /** One field per extra ticket: ticket 2 holds guest 1. */
    guestField: (guestNumber) => `Guest ${guestNumber} email`,
    /* ------------------------------------------------------------- the confirmation */
    sectionTitle: "App invites",
    holderBuyer: "Your invite",
    holderForward: "To pass on",
    /** A guest's invite that went out by email; the hint is "a•••@example.com". */
    emailedTo: (hint) => `Emailed to ${hint}`,
    /** A guest's email that could not be sent: the buyer gets the code to pass on instead. */
    notEmailed: (hint) => `Not emailed to ${hint} — pass it on`,
    redeemed: "Used",
    voided: "No longer valid",
    expired: "Expired",
    useBy: (date) => `Use by ${date}`,
    /* --------------------------------------------------------------------- the emails */
    /** Appended to the buyer's ticket confirmation (purpose `ticket_confirmation`). */
    buyerEmailHeading: "Your GiveHere app invites",
    /** The guest's one message (purpose `ticket_guest_invite`). */
    guestSubject: (eventName) => `Your ticket — ${eventName}`,
};
/** What sits on screen as a label or a heading — five words or fewer each. */
export const TICKET_INVITE_LABELS = [
    TICKET_INVITE_COPY.included,
    TICKET_INVITE_COPY.guestsLabel,
    TICKET_INVITE_COPY.sectionTitle,
    TICKET_INVITE_COPY.holderBuyer,
    TICKET_INVITE_COPY.holderForward,
    TICKET_INVITE_COPY.redeemed,
    TICKET_INVITE_COPY.voided,
    TICKET_INVITE_COPY.expired,
];
/**
 * The email purpose of a guest's ticket and invite. New with GH-884 and **off until the owner adds
 * it to a stack's `EMAIL_PURPOSES`** — engineering never turns a purpose on. While it is off, the
 * checkout does not offer guest emails at all, and every extra invite goes to the buyer to pass on.
 */
export const TICKET_GUEST_EMAIL_PURPOSE = "ticket_guest_invite";
/**
 * Whether a ticket at this price carries an app invite (GH-888, owner 2026-10-04: "paid only").
 *
 * Decided per ticket by the price paid for it — the tier's unit price, in minor units, frozen on the
 * order. A free ticket carries none (a free tier is otherwise an invite printer bounded only by the
 * per-event ceiling); any paid ticket carries one. An order holds one tier today, so every ticket
 * on an order answers alike; the rule is written per ticket so a mixed order would need no new one.
 */
export function ticketPriceCarriesAppInvite(unitPriceMinor) {
    return BigInt(unitPriceMinor) > 0n;
}
/** How long a ticket's app invite is usable after its event (or after it was issued). */
export const TICKET_INVITE_RULES = {
    usableDaysAfterEvent: 30,
};
//# sourceMappingURL=ticket-invite-copy.js.map